import { HttpClient, httpResource } from '@angular/common/http';
import { Service, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, map, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ResponseApi } from '../../../core/dtos/response-api';
import { AuthService } from '../../../core/services/auth/auth.service';
import { ErroCancelamentoSolicitacao } from '../models/minhas-solicitacoes.model';
import { ContestacaoPedidoSicPayload, MeuPedidoSicResponse } from '../models/pedido-sic-acompanhamento.model';
import {
  ERRO_LOCAL_SIC,
  LIMITE_REQUISICOES,
  lerEnvelopeErro,
  rotaPedidoSic,
  SILENCIOSO_SIC,
} from './pedido-sic-acompanhamento.service';

const CIDADAO = `${environment.apiUrl}/sic/cidadao/pedido`;

const SESSAO_EXPIRADA = 'Sua sessão expirou. Entre novamente.';

/**
 * Os pedidos do e-SIC de quem está logado, em `api/sic/cidadao/pedido` — a fonte do e-SIC da aba
 * *Minhas solicitações*, com o cancelamento e a contestação.
 *
 * Como o `MinhasSolicitacoesService`: a lista só é buscada com sessão **e** depois que a aba a
 * pede (`carregar()`), depende do usuário, e as duas ações relêem a lista no sucesso.
 */
@Service()
export class MeusPedidosSicService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  private readonly usuario = toSignal(this.auth.currentUser, {
    initialValue: this.auth.currentUserValue,
  });

  private readonly pedidos = signal(false);

  private readonly listaApi = httpResource<ResponseApi<MeuPedidoSicResponse[]>>(() => {
    if (!this.pedidos() || !this.usuario()?.token) return undefined;

    return { url: CIDADAO, context: ERRO_LOCAL_SIC() };
  });

  readonly falhou = computed(() => this.listaApi.error() !== undefined);

  /**
   * Os pedidos, ou `null` sem sessão, antes do pedido, na primeira carga ou em falha. O
   * `error()` vem ANTES do `value()`: em erro o `value()` lança.
   */
  readonly lista = computed<readonly MeuPedidoSicResponse[] | null>(() => {
    if (this.listaApi.error() !== undefined) return null;

    return this.listaApi.value()?.data ?? null;
  });

  carregar(): void {
    if (this.pedidos()) {
      this.listaApi.reload();
    } else {
      this.pedidos.set(true);
    }
  }

  recarregar(): void {
    this.listaApi.reload();
  }

  cancelar(protocolo: string, motivo: string): Observable<MeuPedidoSicResponse> {
    return this.http
      .post<ResponseApi<MeuPedidoSicResponse>>(`${rotaPedidoSic(CIDADAO, protocolo)}/cancelamento`, { motivo: motivo.trim() }, {
        context: SILENCIOSO_SIC(),
      })
      .pipe(
        map((resposta) => resposta.data),
        tap(() => this.listaApi.reload()),
      );
  }

  contestar(protocolo: string, corpo: ContestacaoPedidoSicPayload): Observable<unknown> {
    return this.http
      .post<ResponseApi<unknown>>(`${rotaPedidoSic(CIDADAO, protocolo)}/contestacao`, corpo, { context: SILENCIOSO_SIC() })
      .pipe(
        map((resposta) => resposta.data),
        tap(() => this.listaApi.reload()),
      );
  }
}

/**
 * Traduz o erro da contestação para o que o modal mostra — o mesmo formato do cancelamento.
 * `404` e `422` dizem que o pedido mudou (fora da janela, outro recurso aceito): a lista é relida
 * para o menu deixar de oferecer *Contestar resposta*.
 */
export function mapearErroContestacao(erro: unknown): ErroCancelamentoSolicitacao {
  const { status, mensagem, detalhes } = lerEnvelopeErro(erro);

  switch (status) {
    case 400:
      return { mensagem: mensagem || 'Não foi possível enviar a contestação com estes dados.', detalhes, recarregar: false };

    case 401:
      return { mensagem: SESSAO_EXPIRADA, detalhes: [], recarregar: false };

    case 404:
      return { mensagem: mensagem || 'Não encontramos este pedido entre as suas solicitações.', detalhes: [], recarregar: true };

    case 422:
      return { mensagem: mensagem || 'Esta resposta não pode mais ser contestada.', detalhes: [], recarregar: true };

    case 429:
      return { mensagem: LIMITE_REQUISICOES, detalhes: [], recarregar: false };

    default:
      return {
        mensagem: 'Não foi possível enviar a contestação agora. Tente novamente em instantes.',
        detalhes: [],
        recarregar: false,
      };
  }
}
